import React, { useState, useEffect, useRef } from "react";
import { X, Save, Calendar, Search } from "lucide-react";
import { toast } from "react-toastify";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { getColombiaDateString, getColombiaTimeString } from "../../../../../../utils/dateUtils";

const RemesaForm = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  remesaId,
  remesaData = {},
  preManifiestoId = null,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    numero_remesa: "",
    cliente: "",
    nit_cliente: "",
    direccion_cliente: "",
    codigo_ciudad_cliente: "",
    nombre_conductor: "",
    cedula_conductor: "",
    placa_vehiculo: "",
    celular: "",
    ruta: "",
    origen: "",
    origen_id: "",
    destino: "",
    destino_id: "",
    direccion_destinatario: "",
    tipo_vehiculo: "",
    peso_mercancia_kg: "",
    tipo_mercancia: "",
    satelital: "",
    satelital_usuario: "",
    satelital_contraseña: "",
    fecha_cargue: getColombiaDateString(),
    hora_cargue: getColombiaTimeString(),
    fecha_descargue: "",
    hora_descargue: "",
    flete: "",
    anticipo: "",
    precinto: "",
    observaciones: "",
    ...remesaData,
  });
  const [loading, setLoading] = useState(false);
  const fechaCargueInputRef = useRef(null);
  const fechaDescargueInputRef = useRef(null);
  
  // City search states
  const [origenSearchTerm, setOrigenSearchTerm] = useState("");
  const [origenSearchResults, setOrigenSearchResults] = useState([]);
  const [showOrigenDropdown, setShowOrigenDropdown] = useState(false);
  const [destinoSearchTerm, setDestinoSearchTerm] = useState("");
  const [destinoSearchResults, setDestinoSearchResults] = useState([]);
  const [showDestinoDropdown, setShowDestinoDropdown] = useState(false);
  const [searchingOrigen, setSearchingOrigen] = useState(false);
  const [searchingDestino, setSearchingDestino] = useState(false);

  useEffect(() => {
    if (remesaId) {
      loadRemesa();
    }
    // Check for pre_manifiesto_id from window (set by parent for quick creation)
    if (
      !remesaId &&
      !preManifiestoId &&
      window.currentPreManifiestoIdForRemesa
    ) {
      setFormData((prev) => ({
        ...prev,
        pre_manifiesto_id: window.currentPreManifiestoIdForRemesa,
      }));
      window.currentPreManifiestoIdForRemesa = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remesaId, preManifiestoId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.city-search-container')) {
        setShowOrigenDropdown(false);
        setShowDestinoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (origenSearchTimeoutRef.current) {
        clearTimeout(origenSearchTimeoutRef.current);
      }
      if (destinoSearchTimeoutRef.current) {
        clearTimeout(destinoSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (preManifiestoId) {
      setFormData((prev) => ({ ...prev, pre_manifiesto_id: preManifiestoId }));
    }
  }, [preManifiestoId]);

  const loadRemesa = async () => {
    try {
      const res = await roldanApi.getRemesaById(
        mantisBusinessId,
        token,
        remesaId
      );
      if (res?.success && res.data) {
        const data = res.data;
        // Helper function to normalize date to YYYY-MM-DD format
        const normalizeDate = (dateValue) => {
          if (!dateValue) return "";
          try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return "";
            return date.toISOString().split("T")[0];
          } catch {
            return "";
          }
        };

        // Helper function to normalize time to HH:mm format
        const normalizeTime = (timeValue) => {
          if (!timeValue) return "";
          if (typeof timeValue === "string") {
            // If already in HH:mm or HH:mm:ss format, extract HH:mm
            const match = timeValue.match(/^(\d{2}):(\d{2})/);
            if (match) return `${match[1]}:${match[2]}`;
          }
          return "";
        };

        // Normalize date fields to YYYY-MM-DD format for HTML date inputs
        // Only include the fields we need for the form to avoid circular references
        const normalizedData = {
          numero_remesa: data.numero_remesa || "",
          cliente: data.cliente || "",
          nit_cliente: data.nit_cliente || "",
          direccion_cliente: data.direccion_cliente || "",
          codigo_ciudad_cliente: data.codigo_ciudad_cliente || "",
          nombre_conductor: data.nombre_conductor || "",
          cedula_conductor: data.cedula_conductor || "",
          placa_vehiculo: data.placa_vehiculo || "",
          celular: data.celular || "",
          ruta: data.ruta || "",
          origen: data.origen || "",
          origen_id: data.origen_id || "",
          destino: data.destino || "",
          destino_id: data.destino_id || "",
          direccion_destinatario: data.direccion_destinatario || "",
          tipo_vehiculo: data.tipo_vehiculo || "",
          peso_mercancia_kg: data.peso_mercancia_kg || "",
          tipo_mercancia: data.tipo_mercancia || "",
          satelital: data.satelital || "",
          satelital_usuario: data.satelital_usuario || "",
          satelital_contraseña: data.satelital_contraseña || "",
          fecha_cargue: normalizeDate(data.fecha_cargue),
          hora_cargue: normalizeTime(data.hora_cargue),
          fecha_descargue: normalizeDate(data.fecha_descargue),
          hora_descargue: normalizeTime(data.hora_descargue),
          flete: data.flete || "",
          anticipo: data.anticipo || "",
          precinto: data.precinto || "",
          observaciones: data.observaciones || "",
          pre_manifiesto_id: data.pre_manifiesto_id || null,
        };
        setFormData(normalizedData);
        // Initialize search terms when loading remesa
        if (normalizedData.origen) {
          setOrigenSearchTerm(normalizedData.origen);
        }
        if (normalizedData.destino) {
          setDestinoSearchTerm(normalizedData.destino);
        }
      }
    } catch (error) {
      console.error("Error loading remesa:", error);
    }
  };

  // Helper function to clean form data for serialization
  const cleanFormData = (data) => {
    const cleaned = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key];
        // Skip undefined, null, and functions
        if (value === undefined || value === null || typeof value === 'function') {
          continue;
        }
        // Convert Date objects to ISO strings
        if (value instanceof Date) {
          cleaned[key] = value.toISOString();
        }
        // For arrays, clean each element
        else if (Array.isArray(value)) {
          cleaned[key] = value.map(item => {
            if (item instanceof Date) return item.toISOString();
            if (typeof item === 'object' && item !== null) {
              try {
                JSON.stringify(item);
                return item;
              } catch {
                return null;
              }
            }
            return item;
          });
        }
        // For objects, try to serialize (will catch circular refs)
        else if (typeof value === 'object') {
          try {
            // Test if it can be serialized
            JSON.stringify(value);
            cleaned[key] = value;
          } catch (e) {
            // Skip circular references
            console.warn(`Skipping non-serializable field: ${key}`, e);
          }
        }
        // Primitives are fine
        else {
          cleaned[key] = value;
        }
      }
    }
    return cleaned;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Seleccionar fecha";
    
    // If dateString is already in YYYY-MM-DD format, parse it directly without timezone conversion
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const monthNames = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ];
      return `${parseInt(day)} de ${monthNames[parseInt(month) - 1]} de ${year}`;
    }
    
    // Fallback for other date formats
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone conversion issues
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    return `${day} de ${monthNames[month]} de ${year}`;
  };

  const handleDateButtonClick = (ref) => {
    ref.current?.showPicker?.() || ref.current?.click();
  };

  // Debounce timers
  const origenSearchTimeoutRef = useRef(null);
  const destinoSearchTimeoutRef = useRef(null);

  // City search functions
  const searchCiudades = async (searchTerm, type) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      if (type === "origen") {
        setOrigenSearchResults([]);
        setShowOrigenDropdown(false);
      } else {
        setDestinoSearchResults([]);
        setShowDestinoDropdown(false);
      }
      return;
    }

    if (type === "origen") {
      setSearchingOrigen(true);
    } else {
      setSearchingDestino(true);
    }

    try {
      const res = await roldanApi.searchCiudades(mantisBusinessId, token, searchTerm.trim());
      if (res?.success && res.data) {
        if (type === "origen") {
          setOrigenSearchResults(res.data);
          setShowOrigenDropdown(true);
        } else {
          setDestinoSearchResults(res.data);
          setShowDestinoDropdown(true);
        }
      } else {
        if (type === "origen") {
          setOrigenSearchResults([]);
          setShowOrigenDropdown(false);
        } else {
          setDestinoSearchResults([]);
          setShowDestinoDropdown(false);
        }
      }
    } catch (error) {
      console.error("Error searching ciudades:", error);
      if (type === "origen") {
        setOrigenSearchResults([]);
        setShowOrigenDropdown(false);
      } else {
        setDestinoSearchResults([]);
        setShowDestinoDropdown(false);
      }
    } finally {
      if (type === "origen") {
        setSearchingOrigen(false);
      } else {
        setSearchingDestino(false);
      }
    }
  };

  const handleOrigenSearch = (value) => {
    setOrigenSearchTerm(value);
    
    // Clear previous timeout
    if (origenSearchTimeoutRef.current) {
      clearTimeout(origenSearchTimeoutRef.current);
    }
    
    if (value.trim().length >= 2) {
      // Debounce search by 300ms
      origenSearchTimeoutRef.current = setTimeout(() => {
        searchCiudades(value, "origen");
      }, 300);
    } else {
      setOrigenSearchResults([]);
      setShowOrigenDropdown(false);
    }
  };

  const handleDestinoSearch = (value) => {
    setDestinoSearchTerm(value);
    
    // Clear previous timeout
    if (destinoSearchTimeoutRef.current) {
      clearTimeout(destinoSearchTimeoutRef.current);
    }
    
    if (value.trim().length >= 2) {
      // Debounce search by 300ms
      destinoSearchTimeoutRef.current = setTimeout(() => {
        searchCiudades(value, "destino");
      }, 300);
    } else {
      setDestinoSearchResults([]);
      setShowDestinoDropdown(false);
    }
  };

  const selectOrigen = (ciudad) => {
    setFormData({
      ...formData,
      origen: ciudad.NOMBRE.trim(),
      origen_id: ciudad.CIUDAD,
    });
    setOrigenSearchTerm(ciudad.NOMBRE.trim());
    setShowOrigenDropdown(false);
    setOrigenSearchResults([]);
  };

  const selectDestino = (ciudad) => {
    setFormData({
      ...formData,
      destino: ciudad.NOMBRE.trim(),
      destino_id: ciudad.CIUDAD,
    });
    setDestinoSearchTerm(ciudad.NOMBRE.trim());
    setShowDestinoDropdown(false);
    setDestinoSearchResults([]);
  };

  // Helper to get simplest city name (shortest)
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const placa = (formData.placa_vehiculo || "").trim();
    if (placa.length > 6) {
      toast.error("La placa debe tener máximo 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      // Clean formData to remove any circular references or non-serializable values
      const cleanedData = cleanFormData(formData);

      let res;
      if (remesaId) {
        res = await roldanApi.updateRemesa(
          mantisBusinessId,
          token,
          remesaId,
          cleanedData
        );
      } else {
        res = await roldanApi.createRemesa(
          mantisBusinessId,
          mantisBusinessUserId,
          token,
          cleanedData
        );
      }
      if (res?.success) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error saving remesa:", error);
      toast.error("Error al guardar la remesa");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col my-4"
        onClick={(e) => {
          // Close dropdowns when clicking outside
          if (!e.target.closest('.city-search-container')) {
            setShowOrigenDropdown(false);
            setShowDestinoDropdown(false);
          }
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">
            {remesaId ? "Editar Remesa" : "Nueva Remesa"}
          </h2>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-2 sm:p-4 overflow-y-auto flex-1 min-h-0"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                N° Remesa (Opcional)
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.numero_remesa}
                onChange={(e) =>
                  setFormData({ ...formData, numero_remesa: e.target.value })
                }
                placeholder="Se generará automáticamente si está vacío"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Cliente (Razón Social)
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.cliente}
                onChange={(e) =>
                  setFormData({ ...formData, cliente: e.target.value })
                }
                placeholder="Ej: Virutex Ilko"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                NIT Cliente
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.nit_cliente}
                onChange={(e) =>
                  setFormData({ ...formData, nit_cliente: e.target.value })
                }
                placeholder="Ej: 901088196"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Dirección Cliente
              </label>
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 min-h-[70px]"
                value={formData.direccion_cliente}
                onChange={(e) =>
                  setFormData({ ...formData, direccion_cliente: e.target.value })
                }
                placeholder="Ej: Calle 123 #45-67"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Código Ciudad Cliente
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.codigo_ciudad_cliente}
                onChange={(e) =>
                  setFormData({ ...formData, codigo_ciudad_cliente: e.target.value })
                }
                placeholder="Ej: 11001000"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Conductor
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.nombre_conductor}
                onChange={(e) =>
                  setFormData({ ...formData, nombre_conductor: e.target.value })
                }
                placeholder="Ej: Olga Lucia Garzon"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                CC Conductor
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.cedula_conductor}
                onChange={(e) =>
                  setFormData({ ...formData, cedula_conductor: e.target.value })
                }
                placeholder="Ej: 52.527.447"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Placa <span className="text-gray-400 font-normal">(máx. 6 caracteres)</span>
              </label>
              <input
                type="text"
                maxLength={6}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.placa_vehiculo}
                onChange={(e) =>
                  setFormData({ ...formData, placa_vehiculo: e.target.value.toUpperCase() })
                }
                placeholder="Ej: GDE474"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Celular
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.celular}
                onChange={(e) =>
                  setFormData({ ...formData, celular: e.target.value })
                }
                placeholder="Ej: 3184053491"
              />
            </div>

            <div className="city-search-container">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Origen <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <Search size={16} className="text-gray-400 ml-3" />
                  <input
                    type="text"
                    className="w-full px-3 py-2 outline-none rounded-lg"
                    value={origenSearchTerm || formData.origen || ""}
                    onChange={(e) => handleOrigenSearch(e.target.value)}
                    onFocus={() => {
                      if (origenSearchResults.length > 0) {
                        setShowOrigenDropdown(true);
                      }
                    }}
                    placeholder="Buscar ciudad de origen..."
                    required
                  />
                  {searchingOrigen && (
                    <div className="px-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>
                {showOrigenDropdown && origenSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {origenSearchResults.map((ciudad, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectOrigen(ciudad)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {ciudad.NOMBRE.trim()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Código: {ciudad.CIUDAD}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="city-search-container">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Destino <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <Search size={16} className="text-gray-400 ml-3" />
                  <input
                    type="text"
                    className="w-full px-3 py-2 outline-none rounded-lg"
                    value={destinoSearchTerm || formData.destino || ""}
                    onChange={(e) => handleDestinoSearch(e.target.value)}
                    onFocus={() => {
                      if (destinoSearchResults.length > 0) {
                        setShowDestinoDropdown(true);
                      }
                    }}
                    placeholder="Buscar ciudad de destino..."
                    required
                  />
                  {searchingDestino && (
                    <div className="px-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>
                {showDestinoDropdown && destinoSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {destinoSearchResults.map((ciudad, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectDestino(ciudad)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {ciudad.NOMBRE.trim()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Código: {ciudad.CIUDAD}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Ruta
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.ruta}
                onChange={(e) =>
                  setFormData({ ...formData, ruta: e.target.value })
                }
                placeholder="Ej: Koba Pereira"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Dirección Destinatario
              </label>
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 min-h-[70px]"
                value={formData.direccion_destinatario}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    direccion_destinatario: e.target.value,
                  })
                }
                placeholder="Ej: Centro logístico Eje Cafetero"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Tipo Vehículo
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.tipo_vehiculo}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_vehiculo: e.target.value })
                }
                placeholder="Ej: Sencillo"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Peso (kg)
              </label>
              <input
                type="number"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.peso_mercancia_kg}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    peso_mercancia_kg: e.target.value,
                  })
                }
                placeholder="Ej: 4000"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Tipo Mercancía
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.tipo_mercancia}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_mercancia: e.target.value })
                }
                placeholder="Ej: Productos de aseo y cocina"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Fecha Cargue
              </label>
              <div className="relative">
                <input
                  ref={fechaCargueInputRef}
                  type="date"
                  name="fecha_cargue"
                  value={formData.fecha_cargue}
                  onChange={(e) =>
                    setFormData({ ...formData, fecha_cargue: e.target.value })
                  }
                  required
                  className="absolute opacity-0 w-0 h-0 pointer-events-none"
                />
                <button
                  type="button"
                  onClick={() => handleDateButtonClick(fechaCargueInputRef)}
                  className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between transition-colors ${
                    formData.fecha_cargue
                      ? "border-gray-300 bg-white text-gray-900 hover:border-blue-400"
                      : "border-gray-300 bg-gray-50 text-gray-500 hover:border-blue-400 hover:bg-white"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <span className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-sm">{formatDate(formData.fecha_cargue)}</span>
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Hora Cargue
              </label>
              <input
                type="time"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.hora_cargue}
                onChange={(e) =>
                  setFormData({ ...formData, hora_cargue: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Fecha Descargue
              </label>
              <div className="relative">
                <input
                  ref={fechaDescargueInputRef}
                  type="date"
                  name="fecha_descargue"
                  value={formData.fecha_descargue}
                  onChange={(e) =>
                    setFormData({ ...formData, fecha_descargue: e.target.value })
                  }
                  className="absolute opacity-0 w-0 h-0 pointer-events-none"
                />
                <button
                  type="button"
                  onClick={() => handleDateButtonClick(fechaDescargueInputRef)}
                  className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between transition-colors ${
                    formData.fecha_descargue
                      ? "border-gray-300 bg-white text-gray-900 hover:border-blue-400"
                      : "border-gray-300 bg-gray-50 text-gray-500 hover:border-blue-400 hover:bg-white"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <span className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-sm">{formatDate(formData.fecha_descargue)}</span>
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Hora Descargue
              </label>
              <input
                type="time"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.hora_descargue}
                onChange={(e) =>
                  setFormData({ ...formData, hora_descargue: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Flete
              </label>
              <input
                type="number"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.flete}
                onChange={(e) =>
                  setFormData({ ...formData, flete: e.target.value })
                }
                placeholder="Ej: 1000000"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Anticipo
              </label>
              <input
                type="number"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.anticipo}
                onChange={(e) =>
                  setFormData({ ...formData, anticipo: e.target.value })
                }
                placeholder="Ej: 700000"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Precinto
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.precinto}
                onChange={(e) =>
                  setFormData({ ...formData, precinto: e.target.value })
                }
                placeholder="Ej: 96870011"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Satélital
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.satelital}
                onChange={(e) =>
                  setFormData({ ...formData, satelital: e.target.value })
                }
                placeholder="Ej: Satrack"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Usuario Satélital
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.satelital_usuario}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    satelital_usuario: e.target.value,
                  })
                }
                placeholder="Ej: OlgaG"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Contraseña Satélital
              </label>
              <input
                type="password"
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                value={formData.satelital_contraseña}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    satelital_contraseña: e.target.value,
                  })
                }
                placeholder="Ej: OlgaG"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Observaciones
              </label>
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 min-h-[70px]"
                value={formData.observaciones}
                onChange={(e) =>
                  setFormData({ ...formData, observaciones: e.target.value })
                }
                placeholder="Observaciones adicionales..."
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50 w-full sm:w-auto"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg w-full sm:w-auto"
              disabled={loading}
            >
              <Save size={16} /> {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RemesaForm;
